import PropTypes from 'prop-types'

import { getCanonicalFylke } from './Fylke'


/*
  The location filter is a single discriminated value, never two independent fields:

    { kind: 'kommune', id, name, fylke } | { kind: 'fylke', name } | null

  The backend accepts either `location` (kommunenummer) or `fylke` (region name), and if
  both are sent the fylke silently wins as the filter. Modelling the selection as one value
  makes that mistake impossible - there is only ever one active location filter.
*/
export const KOMMUNE = 'kommune'
export const FYLKE = 'fylke'

export const createKommuneLocationFilter = kommune => {
  if (!kommune || !kommune.id) return null
  return {
    kind: KOMMUNE,
    id: `${kommune.id}`,
    name: kommune.name || '',
    fylke: kommune.fylke || '',
  }
}

/*
  Takes a fylke name that is already known to be canonical - either picked from the list the api
  returned, or validated against it with isValidFylke. Validation lives at the edges (the picker
  and url rehydration) because the canonical list is fetched, not hardcoded.
*/
export const createFylkeLocationFilter = name => {
  const trimmed = (name || '').toString().trim()
  if (!trimmed) return null
  return {
    kind: FYLKE,
    name: trimmed,
  }
}

/*
  Accepts an already-built location filter, or a raw kommune payload from the location api /
  the CMS ({ id, name, fylke }), or a region-only value ({ fylke } / { name }), and returns a
  well-formed location filter. Anything unrecognised becomes null.
*/
export const normalizeLocationFilter = value => {
  if (!value) return null
  if (value.kind === FYLKE) return createFylkeLocationFilter(value.name)
  if (value.kind === KOMMUNE || value.id) return createKommuneLocationFilter(value)
  return createFylkeLocationFilter(value.fylke || value.name)
}

export const isKommuneFilter = locationFilter => !!locationFilter && locationFilter.kind === KOMMUNE
export const isFylkeFilter = locationFilter => !!locationFilter && locationFilter.kind === FYLKE

/*
  Params for the SEARCH BODY.

  A kommune is sent together with its fylke on purpose: the backend then filters on the whole
  region while `location` drives the ordering, so picking Kvam shows every artist available in
  that region with the Kvam locals ranked first. A fylke selection sends `fylke` alone.

  The url carries the same pair, so ?location=4622&fylke=Vestland round-trips back to the same
  Kvam selection. Switching kommune -> fylke drops `location`, so no stale key survives.
*/
export const getLocationFilterParams = locationFilter => {
  if (isKommuneFilter(locationFilter) && locationFilter.id) {
    const params = { location: locationFilter.id }
    if (locationFilter.fylke) params.fylke = locationFilter.fylke
    return params
  }
  if (isFylkeFilter(locationFilter) && locationFilter.name) {
    return { fylke: locationFilter.name }
  }
  return {}
}

/*
  Reads a location filter back out of the url query, mirroring how the params are written:

    location + fylke -> a kommune selection, carrying the fylke it sits in
    fylke alone      -> a whole-region selection
    location alone   -> a kommune whose fylke is not known yet (older links)

  An unrecognised fylke is dropped silently - pass the canonical names from the store as
  `fylkeNames` to have it validated; without them the name is trusted. A kommune is rehydrated
  without a display name, so fetch it with kommuneById.
*/
export const getLocationFilterFromQuery = (query, fylkeNames = null) => {
  if (!query) return null

  const canonicalFylke = () => {
    if (!query.fylke) return ''
    if (!fylkeNames) return query.fylke
    return getCanonicalFylke(fylkeNames, query.fylke)
  }

  if (query.location) {
    return createKommuneLocationFilter({
      id: query.location,
      name: '',
      fylke: canonicalFylke(),
    })
  }
  if (query.fylke) {
    const canonical = canonicalFylke()
    return canonical ? createFylkeLocationFilter(canonical) : null
  }
  return null
}

// Label for a chip / active filter: a region reads distinctly from a kommune.
export const getLocationFilterLabel = locationFilter => {
  if (isFylkeFilter(locationFilter)) {
    return `${locationFilter.name} (hele fylket)`
  }
  if (isKommuneFilter(locationFilter)) {
    return locationFilter.name || ''
  }
  return ''
}

export const LocationFilterShape = PropTypes.oneOfType([
  PropTypes.shape({
    kind: PropTypes.oneOf([KOMMUNE]).isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    fylke: PropTypes.string,
  }),
  PropTypes.shape({
    kind: PropTypes.oneOf([FYLKE]).isRequired,
    name: PropTypes.string.isRequired,
  }),
])
